/**
 * Guide for lead-capture staff (CRM) (AUTONOMY · Layer 3).
 */
import { msg } from "@lingui/core/macro";
import { Users } from "@fleet/icons";

import type { HelpGuide } from "../types";
import { Bullets, GoTo, Note, Steps } from "../components/help-prose";

/**
 * Literal example of the WhatsApp template variable syntax, passed
 * as the VALUE of an ICU placeholder instead of written inline in the `msg`: a
 * `{{nombre}}` inside the text confuses ICU for a real placeholder with no
 * value and it gets replaced with nothing (see `email-template-editor.tsx`).
 */
// oxlint-disable-next-line lingui/no-unlocalized-strings -- literal sample, not UI copy
const WHATSAPP_VAR_NOMBRE = "{{nombre}}";
// oxlint-disable-next-line lingui/no-unlocalized-strings -- literal sample, not UI copy
const WHATSAPP_VAR_CURSO = "{{curso}}";

export const crmGuide: HelpGuide = {
  key: "help.crm",
  slug: "captacion",
  title: msg`Recruitment guide (CRM)`,
  description: msg`Follow every prospective student from first contact to enrollment: pipeline, follow-ups, WhatsApp, segments and campaigns.`,
  audience: msg`Recruitment and front-desk staff`,
  icon: Users,
  sections: [
    {
      id: "pipeline",
      title: msg`Leads and the pipeline`,
      summary: msg`A lead is a prospective student. Drag leads across the kanban stages: new → contacted → visit → enrolled (or lost).`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Leads arrive from the public website form (with campaign UTM data) or are created by hand with phone, ID and course of interest.`,
              msg`Each card shows the source and assignee; drag it to update the stage.`,
            ]}
          />
          <GoTo to="/crm" label={msg`Open the CRM board`} />
        </>
      ),
    },
    {
      id: "seguimiento",
      title: msg`Follow-up timeline`,
      summary: msg`Every call, WhatsApp message, visit or note is logged on the lead's timeline, so anyone can pick up the conversation.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open a lead card to see its timeline.`,
              msg`Log each interaction: call, WhatsApp, visit or note.`,
              msg`Assign the lead to a teammate so ownership is clear.`,
            ]}
          />
          <GoTo to="/crm" label={msg`Open the CRM board`} />
        </>
      ),
    },
    {
      id: "whatsapp",
      title: msg`WhatsApp templates`,
      summary: msg`Reusable message templates with variables like ${WHATSAPP_VAR_NOMBRE} or ${WHATSAPP_VAR_CURSO} open WhatsApp with the text pre-filled.`,
      body: () => (
        <>
          <Bullets
            items={[
              msg`Manage templates in CRM → Templates; variables in double braces are replaced with the lead's data.`,
              msg`WhatsApp buttons on lead cards and overdue invoices use these templates — no copy-pasting numbers.`,
            ]}
          />
          <Note
            msg={msg`Messages are sent from your own WhatsApp — the platform builds the link, it does not send on your behalf.`}
          />
          <GoTo to="/crm/templates" label={msg`Open Templates`} />
        </>
      ),
    },
    {
      id: "segmentos",
      title: msg`Segments and email campaigns`,
      summary: msg`Segments are saved audience filters (interest, stage, debt, inactivity). Email campaigns can target any segment.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Build a segment in CRM → Segments combining filters.`,
              msg`Create an email campaign in Admin → Email → Campaigns and pick the segment as audience.`,
              msg`Schedule it or send it now; templates are edited in Admin → Email.`,
            ]}
          />
          <GoTo to="/crm/segments" label={msg`Open Segments`} />
        </>
      ),
    },
    {
      id: "conversion",
      title: msg`Converting a lead into a student`,
      summary: msg`When a lead enrolls, convert it: the platform creates the student account and links it to the original lead for funnel analytics.`,
      body: () => (
        <>
          <Steps
            items={[
              msg`Open the lead and use Convert when they decide to enroll.`,
              msg`Complete the enrollment in the cohort (capacity and waitlist are enforced automatically).`,
              msg`The funnel report in Admin → Metrics shows conversion by source and campaign.`,
            ]}
          />
          <GoTo to="/crm" label={msg`Open the CRM board`} />
        </>
      ),
    },
  ],
};
